import { useAuth } from '@on3/ui-lib/src/contexts/AuthProvider';
import { useCallback, useEffect, useRef } from 'react';

import styles from './StnPlayer.module.scss';

declare global {
  interface Window {
    setupStnApi?: (
      getPlayerApi: (player: HTMLDivElement) => StnPlayerApi,
    ) => void;
    voltax: {
      players: StnPlayerType[];
    };
  }
}

type StnPlayerType = {
  destroyPlayer: () => void;
};

type StnPlayerApi = {
  float: boolean;
};

const SCRIPT_ID = 'stn-player-script';
const PLAYER_ID = 'c24ahl26';

const loadStnScript = (playerId: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (document.getElementById(SCRIPT_ID)) {
      return resolve();
    }

    const script = document.createElement('script');

    script.src = `//embed.sendtonews.com/player3/embedcode.js?fk=${playerId}`;
    script.async = true;
    script.id = SCRIPT_ID;
    script.className = 'stn-player';

    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load StnPlayer script'));

    document.body.appendChild(script);
  });
};

export const StnPlayer = () => {
  const { user } = useAuth();
  const stnPlayerRef = useRef<HTMLDivElement>(null);
  const isPremiumUser = user?.st?.includes('premium') || false;

  // Setup the StnPlayer API using a global window function based on docs
  const setupStnApi = useCallback(
    (getPlayerApi: (player: HTMLDivElement) => StnPlayerApi) => {
      const player = stnPlayerRef.current as HTMLDivElement;
      const api = getPlayerApi(player);

      api.float = user?.has ? false : true;
    },
    [user?.has],
  );

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    const ref = document.referrer.toLowerCase();
    const isTwitterReferral = ua.includes('twitter') || ref.includes('t.co');

    if (isTwitterReferral || isPremiumUser) {
      return;
    }

    // Assign the setup function to the global window object
    window.setupStnApi = setupStnApi;

    // Load the StnPlayer script
    loadStnScript(PLAYER_ID)
      .then(() => {
        // Silence - it works
      })
      .catch((error) => {
        console.error('StnPlayer script failed to load:', error);
      });

    return () => {
      window.setupStnApi = undefined;
    };
  }, [isPremiumUser, user?.has, setupStnApi]);

  if (isPremiumUser) return null;

  return (
    <div className={styles.container}>
      <div
        className="stnPlayer"
        data-s2n-api="setupStnApi"
        data-stn-player={PLAYER_ID}
        ref={stnPlayerRef}
      />
    </div>
  );
};
